import React from "react";
import "./CourseDetailSectionStyles.css";
import {
  FaBookOpen,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaComments,
  FaCrown,
  FaGift,
  FaGraduationCap,
} from "react-icons/fa";

import type { CourseDetail, CourseLevelKey } from "./CoursesDetails";

type CourseDetailSectionProps = {
  courseLevel: CourseLevelKey;
  course: CourseDetail;
  courseLabel: string;
};

type GermanCourseText = {
  title: string;
  subtitle: string;
  description: string;
  duration: string;
  sessions: string;
  language: string;
  price: string;
  features: string[];
  teacherNote: string;
};

const GERMAN_COURSE_TEXTS: Record<CourseLevelKey, GermanCourseText> = {
  A1: {
    title: "A1-DEUTSCHKURS",
    subtitle: "Gruppenkurs für Anfänger",
    description:
      "Starte noch heute deine Reise mit Deutsch. Sprich Deutsch mit Selbstvertrauen.",
    duration: "2 Monate",
    sessions: "2x pro Woche à 2 Stunden",
    language: "Deutsch & Englisch",
    price: "100 €",
    features: [
      "Deutsch lernen mit einem vollständigen interaktiven Kurs",
      "Sprechen, Hören, Lesen und Schreiben gezielt verbessern",
      "10 PDF-Lernbücher komplett kostenlos enthalten",
      "1 Jahr kostenloser Premium-Zugang zur mobilen App inklusive",
    ],
    teacherNote:
      "Der Unterricht wird klar, freundlich und Schritt für Schritt erklärt.",
  },
  A2: {
    title: "A2-DEUTSCHKURS",
    subtitle: "Gruppenkurs für Grundstufe",
    description:
      "Erweitere deinen Wortschatz und kommuniziere sicherer im Alltag.",
    duration: "2 Monate",
    sessions: "2x pro Woche à 2 Stunden",
    language: "Deutsch & Englisch",
    price: "125 €",
    features: [
      "Alltagssprache sicherer verstehen und anwenden",
      "Sprechen, Hören, Lesen und Schreiben gezielt verbessern",
      "10 PDF-Lernbücher komplett kostenlos enthalten",
      "1 Jahr kostenloser Premium-Zugang zur mobilen App inklusive",
    ],
    teacherNote:
      "Der Unterricht hilft dir, Alltagssituationen sicherer auf Deutsch zu meistern.",
  },
  B1: {
    title: "B1-DEUTSCHKURS",
    subtitle: "Gruppenkurs für Mittelstufe",
    description:
      "Meistere alltägliche Situationen und diskutiere über vertraute Themen.",
    duration: "3 Monate",
    sessions: "2x pro Woche à 2 Stunden",
    language: "Deutsch & Englisch",
    price: "150 €",
    features: [
      "Eigene Meinungen klar formulieren und begründen",
      "Sprechen, Hören, Lesen und Schreiben gezielt verbessern",
      "10 PDF-Lernbücher komplett kostenlos enthalten",
      "1 Jahr kostenloser Premium-Zugang zur mobilen App inklusive",
    ],
    teacherNote:
      "Der Unterricht stärkt deine Sicherheit beim Sprechen, Schreiben und Verstehen.",
  },
  B2: {
    title: "B2-DEUTSCHKURS",
    subtitle: "Gruppenkurs für Oberstufe",
    description:
      "Verstehe komplexe Texte und diskutiere fließend über abstrakte Themen.",
    duration: "3 Monate",
    sessions: "2x pro Woche à 2 Stunden",
    language: "Deutsch & Englisch",
    price: "175 €",
    features: [
      "Komplexe Texte verstehen und strukturiert beantworten",
      "Sprechen, Hören, Lesen und Schreiben gezielt verbessern",
      "10 PDF-Lernbücher komplett kostenlos enthalten",
      "1 Jahr kostenloser Premium-Zugang zur mobilen App inklusive",
    ],
    teacherNote:
      "Der Unterricht bereitet dich auf anspruchsvollere Gespräche und Texte vor.",
  },
};

const CourseDetailSection: React.FC<CourseDetailSectionProps> = ({
  courseLevel,
  course,
  courseLabel,
}) => {
  const germanText = GERMAN_COURSE_TEXTS[courseLevel];

  return (
    <section className="course-detail-column">
      <article className="course-detail-card">
        <div className="course-detail-top">
          <div className="course-detail-badge">
            <FaCrown />
            Online-Gruppenkurs
          </div>

          <div className="course-detail-title-row">
            <div>
              <p>{germanText.subtitle}</p>
              <h1>{germanText.title}</h1>
            </div>

            <strong>{germanText.price}</strong>
          </div>

          <p className="course-detail-description">
            {germanText.description}
          </p>
        </div>

        <div className="course-detail-facts">
          <div className="course-detail-fact">
            <span>
              <FaCalendarAlt />
            </span>

            <div>
              <strong>{germanText.duration}</strong>
              <p>Kursdauer</p>
            </div>
          </div>

          <div className="course-detail-fact">
            <span>
              <FaClock />
            </span>

            <div>
              <strong>{germanText.sessions}</strong>
              <p>Unterricht</p>
            </div>
          </div>

          <div className="course-detail-fact">
            <span>
              <FaComments />
            </span>

            <div>
              <strong>{germanText.language}</strong>
              <p>Erklärsprache</p>
            </div>
          </div>
        </div>

        <div className="course-detail-body">
          <section className="course-detail-section">
            <div className="course-detail-section-heading">
              <FaGraduationCap />

              <div>
                <span>{courseLabel || courseLevel}</span>
                <h2>Kursinhalt</h2>
              </div>
            </div>

            <div className="course-detail-feature-list">
              {germanText.features.map((feature) => (
                <div key={feature} className="course-detail-feature">
                  <FaCheckCircle />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="course-detail-section course-detail-books">
            <div className="course-detail-section-heading">
              <FaBookOpen />

              <div>
                <span>Kostenlos inklusive</span>
                <h2>10 PDF-Bücher</h2>
              </div>
            </div>

            <p>
              Alle Bücher sind für Kursteilnehmer kostenlos enthalten.
              Gesamtwert: <strong>100 €</strong>
            </p>

            <div className="course-detail-book-list">
              {course.books.map((book) => (
                <span key={book.id}>
                  {book.name}
                  <small>Deutsch</small>
                </span>
              ))}
            </div>

            <div className="course-detail-gift">
              <FaGift />

              <div>
                <span>Bonus</span>
                <strong>PDF-Bücher inklusive</strong>
              </div>
            </div>
          </section>

          <section className="course-detail-section course-detail-teacher">
            <div className="course-detail-section-heading">
              <FaCrown />

              <div>
                <span>Unterricht</span>
                <h2>Klare Erklärungen</h2>
              </div>
            </div>

            <p>{germanText.teacherNote}</p>
          </section>
        </div>
      </article>
    </section>
  );
};

export default CourseDetailSection;